import './TrayList.scss'
import {
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Skeleton,
} from '@mui/material'
import {
  Add,
  Delete,
  Edit,
  Category,
  Layers,
  Image as ImageIcon,
  LocationOn,
  Business,
} from '@mui/icons-material'
import { LoadingButton } from 'components/mui'
import { useAddTrayContext } from '../AddTray.context'
import { useTrayStepContext } from '../AddTray.logic'
import { useState } from 'react'
import AlertDialog from 'components/AlertDialog/AlertDialog'
import useTrayConfiguration from 'views/TrayConfiguration/TrayConfiguration.logic'
import useDTMS3Image from 'lib/utils/useDTMS3Image'

// Create a reusable TrayImage component
const TrayImage = ({ imageUrl }: { imageUrl?: string }) => {
  const { imageUrl: trayImageSrc, isLoading } = useDTMS3Image({
    url: imageUrl,
    defaultImage: '',
  })

  if (!imageUrl) {
    return (
      <div className="tray-image-container no-image">
        <ImageIcon />
      </div>
    )
  }

  return (
    <div className="tray-image-container">
      {isLoading ? (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
          className="tray-thumbnail-skeleton"
        />
      ) : (
        <img src={trayImageSrc} alt="Tray" className="tray-thumbnail" />
      )}
    </div>
  )
}

const TrayList = () => {
  const {
    trays,
    removeTrayItem,
    loadTrayItem,
    resetAddTrayContext,
    setCurrentTrayIndex,
  } = useAddTrayContext()

  const { nextStep, prevStep, goToStep } = useTrayStepContext()
  const { findLocation } = useTrayConfiguration()
  const [trayToDelete, setTrayToDelete] = useState<number | null>(null)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)

  const handleAddNewTray = () => {
    resetAddTrayContext()
    setCurrentTrayIndex(-1)
    goToStep(0) // Go to first step (tray details)
  }

  const handleEditTray = (index: number) => {
    loadTrayItem(index)
    goToStep(0) // Go to first step (tray details)
  }

  const handleDeleteTray = (index: number) => {
    setTrayToDelete(index)
    setShowDeleteConfirmation(true)
  }

  const handleConfirmDelete = () => {
    if (trayToDelete !== null) {
      removeTrayItem(trayToDelete)
      setShowDeleteConfirmation(false)
      setTrayToDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false)
    setTrayToDelete(null)
  }

  const handleReview = () => {
    nextStep()
  }

  return (
    <div className="tray-list-step">
      <AlertDialog
        isOpen={showDeleteConfirmation}
        mode="error"
        title="Delete Tray"
        description="Are you sure you want to delete this tray? This action cannot be undone."
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        primaryButtonAction={handleConfirmDelete}
        secondaryButtonAction={handleCancelDelete}
        zIndex={9999}
      />

      <div className="tray-list-content">
        <div className="header_wrapper">
          <Typography variant="h5">Trays ({trays.length})</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddNewTray}
          >
            Add New Tray For This Case
          </Button>
        </div>

        {trays.length === 0 ? (
          <div className="empty-state">
            <Add fontSize="large" />
            <Typography variant="body1" color="textSecondary">
              No trays added yet. Click "Add New Tray For This Case" to get
              started.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddNewTray}
              className="add-tray-button"
            >
              Add New Tray
            </Button>
          </div>
        ) : (
          <List className="tray-list">
            {trays.map((tray, index) => (
              <ListItem key={tray.id} className="tray-list-item">
                <TrayImage imageUrl={tray.parentTrayImage?.src} />
                <ListItemText
                  primary={
                    <Typography variant="h6">
                      Description: {tray.trayDescription || `Tray ${index + 1}`}
                    </Typography>
                  }
                  secondary={
                    <div className="tray-details">
                      <div className="detail-row">
                        <Category fontSize="small" />
                        <Typography variant="body2" color="textSecondary">
                          <span className="label">Type:</span>
                          <span className="highlight">
                            {tray.selectedTrayType || tray.selectedTrayCategory}
                          </span>
                        </Typography>
                      </div>

                      <div className="detail-row">
                        <Business fontSize="small" />
                        <Typography variant="body2" color="textSecondary">
                          <span className="label">Vendor:</span>
                          <span className="highlight">{tray.trayVendor}</span>
                        </Typography>
                      </div>

                      <div className="detail-row">
                        <LocationOn fontSize="small" />
                        <Typography variant="body2" color="textSecondary">
                          <span className="label">Location:</span>
                          <span className="highlight">
                            {tray.selectedLocationId
                              ? findLocation(tray.selectedLocationId)?.name
                              : 'Not specified'}
                          </span>
                        </Typography>
                      </div>

                      {tray.hasSubTrays && (
                        <div className="detail-row">
                          <Layers fontSize="small" />
                          <Typography variant="body2" color="textSecondary">
                            <span className="label">Sub-trays:</span>
                            <span className="highlight">
                              {tray.subTrays.length}
                            </span>
                          </Typography>
                        </div>
                      )}
                    </div>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Edit tray">
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      onClick={() => handleEditTray(index)}
                      className="edit-button"
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete tray">
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDeleteTray(index)}
                      className="delete-button"
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </div>

      <div className="buttons_container">
        <Button variant="outlined" onClick={prevStep}>
          Back
        </Button>
        {trays.length > 0 && (
          <LoadingButton
            variant="contained"
            onClick={handleReview}
            className="review-button"
          >
            Review All Trays
          </LoadingButton>
        )}
      </div>
    </div>
  )
}

export default TrayList
